import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Download, FileText, CheckCircle2, ArrowLeft, FileType2, ShieldCheck, X } from "lucide-react";
import SharedFooter from "@/components/SharedFooter";
import { clearParentLead, readParentLead, saveParentLead } from "@/lib/parentLead";

const DEFAULT_A4  = "/parent-guide/WeKIT-Career-Clarity-360-Parent-Guide-A4.pdf";
const DEFAULT_LTR = "/parent-guide/WeKIT-Career-Clarity-360-Parent-Guide-Letter.pdf";
const DEFAULT_PPT = "/parent-guide/WeKIT-Career-Clarity-360-Parent-Guide.pptx";
const P1  = "/parent-guide/preview-page-1.jpg";
const P2  = "/parent-guide/preview-page-2.jpg";

const AGE_RANGES = ["Under 8", "8–12", "13–15", "16–18", "18–22", "22+"];

const SURFACE = "parent-guide";
const SESSION_KEY = "wekit_session_id";
const CONSENT_KEY = "wekit_pg_analytics_consent"; // "granted" | "denied"

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `${Date.now()}`;
  }
}

type Consent = "granted" | "denied" | "unknown";
function readConsent(): Consent {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : "unknown";
  } catch {
    return "unknown";
  }
}

export default function ParentGuide() {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [age, setAge] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [recalled, setRecalled] = useState(false);
  const [consent, setConsent] = useState<Consent>("unknown");
  const [urls, setUrls] = useState({ a4: DEFAULT_A4, letter: DEFAULT_LTR, pptx: DEFAULT_PPT });
  const consentRef = useRef<Consent>("unknown");
  const viewTracked = useRef(false);

  // Read consent. Download links use the published static assets — asset
  // metadata is admin-only and never read directly by the browser.
  useEffect(() => {
    const c = readConsent();
    setConsent(c);
    consentRef.current = c;

    // Already told us who they are (here or on the home page)? Don't ask again.
    const known = readParentLead();
    if (known) {
      setName(known.name);
      setEmail(known.email);
      setMobile(known.mobile ?? "");
      setAge(known.childAge ?? "");
      setRecalled(true);
      setUnlocked(true);
    }
  }, []);


  const trackEvent = async (event: string, props: Record<string, unknown> = {}) => {
    if (consentRef.current !== "granted") return; // gated by explicit consent
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("analytics_events").insert({
        event,
        surface: SURFACE,
        props: props as never,
        path: typeof window !== "undefined" ? window.location.pathname : null,
        session_id: getSessionId(),
        user_id: user?.id ?? null,
      });
    } catch { /* analytics must never break UX */ }
  };

  // Fire page view once consent is granted (either initially or later via banner).
  useEffect(() => {
    if (consent !== "granted" || viewTracked.current) return;
    viewTracked.current = true;
    void trackEvent("parent_guide_view", {
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);

  const acceptConsent = () => {
    try { localStorage.setItem(CONSENT_KEY, "granted"); } catch { /* noop */ }
    consentRef.current = "granted";
    setConsent("granted");
  };
  const declineConsent = () => {
    try { localStorage.setItem(CONSENT_KEY, "denied"); } catch { /* noop */ }
    consentRef.current = "denied";
    setConsent("denied");
  };

  const formatLabel = (href: string) =>
    href === urls.a4 ? "a4_pdf" : href === urls.letter ? "letter_pdf" : href === urls.pptx ? "pptx" : "unknown";

  const triggerDownload = (href: string, source: "auto" | "button" = "button") => {
    void trackEvent("parent_guide_download", { format: formatLabel(href), source });
    const a = document.createElement("a");
    a.href = href;
    a.download = href.split("/").pop() ?? "WeKIT-Parent-Guide";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePreviewClick = (page: string) => {
    void trackEvent("parent_guide_preview_click", { page });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast({ title: "Missing details", description: "Please enter your name and email.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    void trackEvent("parent_guide_form_submit", {
      has_mobile: Boolean(mobile.trim()),
      child_age_range: age || null,
    });

    // Persist lead + send confirmation email via edge function.
    const { data, error } = await supabase.functions.invoke("parent-guide-submit", {
      body: {
        parent_name: name.trim(),
        email: email.trim(),
        mobile: mobile.trim() || null,
        child_age_range: age || null,
        source: "parent-guide-page",
        user_agent: navigator.userAgent.slice(0, 400),
      },
    });
    setSubmitting(false);

    if (error || (data as any)?.error) {
      void trackEvent("parent_guide_form_error", { message: error?.message ?? (data as any)?.error });
      toast({
        title: "Something went wrong",
        description: "Please check your details and try again.",
        variant: "destructive",
      });
      return;
    }
    // If edge function returned fresher URLs, use them.
    const returned = (data as any)?.urls;
    if (returned) setUrls((prev) => ({ ...prev, ...returned }));

    void trackEvent("parent_guide_form_success", { child_age_range: age || null });
    saveParentLead({
      name: name.trim(),
      email: email.trim(),
      mobile: mobile.trim() || null,
      childAge: age || null,
    });
    setRecalled(false);
    setUnlocked(true);
    triggerDownload(returned?.a4 ?? urls.a4, "auto");
    toast({ title: "Your guide is downloading", description: "We've also emailed you the download links." });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>WeKIT™ Career Clarity 360 — Parent Guide (Free Download)</title>
        <meta name="description" content="Download the free 2-page WeKIT™ Career Clarity 360 Parent Guide — help your child discover strengths and explore future-proof careers." />
        <link rel="canonical" href="https://www.wekit.ai/parent-guide" />
      </Helmet>

      {/* Header */}
      <div className="border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to WeKIT™
          </Link>
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Parent Guide · WCC360</span>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-4 py-10 md:py-16">
        <header className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-accent-foreground">
            <FileText className="h-3.5 w-3.5" /> Free 2-page parent guide
          </span>
          <h1 className="mt-5 text-balance text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
            The future doesn't need better marks.
            <span className="block text-primary">It needs better humans.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-base text-muted-foreground md:text-lg">
            A short, evidence-based guide for parents on how to help your child discover strengths,
            explore future-proof careers, and build the skills that matter in an AI-powered world.
          </p>
        </header>

        <section className="mt-12 grid gap-10 lg:grid-cols-[1.15fr_1fr]">
          {/* Preview */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Preview before you download
            </h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {[{ src: P1, label: "Page 1" }, { src: P2, label: "Page 2" }].map((p) => (
                <figure key={p.src} className="overflow-hidden rounded-2xl border border-border bg-muted/30 shadow-sm">
                  <button
                    type="button"
                    onClick={() => handlePreviewClick(p.label)}
                    className="block w-full text-left"
                    aria-label={`Preview ${p.label} of the WeKIT Career Clarity 360 Parent Guide`}
                  >
                    <img
                      src={p.src}
                      alt={`WeKIT Career Clarity 360 Parent Guide — ${p.label}`}
                      className="block h-auto w-full transition-transform duration-300 hover:scale-[1.01]"
                      loading="lazy"
                      width={1075}
                      height={1518}
                    />
                  </button>
                  <figcaption className="border-t border-border/60 px-4 py-2 text-xs text-muted-foreground">
                    {p.label}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          {/* Form / Download */}
          <aside className="lg:sticky lg:top-8 lg:self-start">
            <Card className="rounded-2xl border-border/70 p-6 md:p-8">
              {!unlocked ? (
                <>
                  <h2 className="text-xl font-semibold tracking-tight">Get your free copy</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Enter your details — your download starts immediately and we'll email you the links.
                  </p>
                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="pg-name">Parent name</Label>
                      <Input id="pg-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" autoComplete="name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="pg-email">Email</Label>
                      <Input id="pg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="pg-mobile">Mobile <span className="text-muted-foreground">(optional)</span></Label>
                        <Input id="pg-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 90000 00000" autoComplete="tel" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="pg-age">Child age</Label>
                        <Select value={age} onValueChange={setAge}>
                          <SelectTrigger id="pg-age"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {AGE_RANGES.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" size="lg" disabled={submitting} className="h-12 w-full text-base">
                      <Download className="mr-2 h-4 w-4" />
                      {submitting ? "Preparing your guide…" : "Download the guide"}
                    </Button>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      We'll email your download links and occasional career insights from WeKIT™. Unsubscribe anytime.
                      By downloading you agree to our <Link to="/privacy" className="underline">privacy policy</Link>.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h2 className="mt-4 text-xl font-semibold tracking-tight">
                    {recalled ? "Your guide is ready" : "Your guide is on its way"}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {recalled
                      ? `We already have your details${email ? ` (${email})` : ""} — just pick a copy below.`
                      : `Downloading now — we've also emailed a copy of the links to ${email}.`}
                  </p>
                  <div className="mt-5 grid gap-2">
                    <Button size="lg" className="h-12 justify-start" onClick={() => triggerDownload(urls.a4)}>
                      <Download className="mr-2 h-4 w-4" /> Download the guide
                    </Button>
                    <Button variant="outline" size="lg" className="h-11 justify-start" onClick={() => triggerDownload(urls.letter)}>
                      <FileText className="mr-2 h-4 w-4" /> Download for US paper size
                    </Button>
                    <Button variant="outline" size="lg" className="h-11 justify-start" onClick={() => triggerDownload(urls.pptx)}>
                      <FileType2 className="mr-2 h-4 w-4" /> Download as slides
                    </Button>
                  </div>
                  <Button asChild variant="link" className="mt-4">
                    <Link to="/payment">Explore WeKIT™ Career Clarity 360 →</Link>
                  </Button>
                  {recalled && (
                    <button
                      type="button"
                      className="mx-auto mt-1 block min-h-11 text-xs text-muted-foreground underline"
                      onClick={() => {
                        clearParentLead();
                        setRecalled(false);
                        setUnlocked(false);
                      }}
                    >
                      Not you? Use different details
                    </button>
                  )}
                </div>
              )}
            </Card>

            <div className="mt-4 rounded-2xl border border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">What's inside</p>
              <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                <li>• The 7 areas every child needs</li>
                <li>• 12-month Career Growth Journey</li>
                <li>• 5 check-ins across the year</li>
                <li>• 10 future-positive careers</li>
                <li>• Signs your child needs clarity</li>
                <li>• How to start the conversation</li>
              </ul>
            </div>
          </aside>
        </section>
      </main>

      {/* Analytics consent banner */}
      {consent === "unknown" && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Analytics consent"
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-2xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur md:inset-x-auto md:right-6 md:bottom-6"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="flex-1 text-sm">
              <p className="font-medium text-foreground">Help us improve this guide</p>
              <p className="mt-1 text-muted-foreground">
                We'd like to record anonymous events (page views, preview clicks, downloads) to see what parents find useful.
                We won't track anything until you say yes. See our{" "}
                <Link to="/privacy" className="underline">privacy policy</Link>.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" onClick={acceptConsent}>Accept analytics</Button>
                <Button size="sm" variant="outline" onClick={declineConsent}>Decline</Button>
              </div>
            </div>
            <button
              type="button"
              onClick={declineConsent}
              aria-label="Dismiss and decline analytics"
              className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <SharedFooter />
    </div>
  );
}
